import { supabase } from '../lib/supabase';

// ─── Budget ───────────────────────────────────────────────────────────────────
export async function fetchCurrentBudget(userId) {
  const currentMonth = new Date()?.toISOString()?.slice(0, 7);
  const { data, error } = await supabase?.from('budgets')?.select('*')?.eq('user_id', userId)?.eq('month_year', currentMonth)?.order('created_at', { ascending: false })?.limit(1)?.maybeSingle();
  if (error) throw error;
  return data;
}

// ─── Spending Categories ──────────────────────────────────────────────────────
export async function fetchSpendingCategories(userId) {
  const { data, error } = await supabase?.from('spending_categories')?.select('*')?.eq('user_id', userId)?.order('monthly_limit', { ascending: false });
  if (error) throw error;
  return data || [];
}

// ─── Transactions ─────────────────────────────────────────────────────────────
export async function fetchRecentTransactions(userId, limit = 10) {
  const { data, error } = await supabase?.from('transactions')?.select('*, spending_categories(name, icon, color)')?.eq('user_id', userId)?.order('transaction_date', { ascending: false })?.order('created_at', { ascending: false })?.limit(limit);
  if (error) throw error;
  return data || [];
}

export async function insertTransaction(userId, tx) {
  const { data, error } = await supabase?.from('transactions')?.insert({ user_id: userId, ...tx })?.select()?.single();
  if (error) throw error;
  return data;
}

// ─── Financial Goals ──────────────────────────────────────────────────────────
export async function fetchFinancialGoals(userId) {
  const { data, error } = await supabase?.from('financial_goals')?.select('*')?.eq('user_id', userId)?.eq('goal_status', 'active')?.order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

// ─── Summary Stats ────────────────────────────────────────────────────────────
export async function fetchSummaryStats(userId) {
  const currentMonth = new Date()?.toISOString()?.slice(0, 7);

  const [budgetRes, categoriesRes, goalsRes] = await Promise.all([
    supabase?.from('budgets')?.select('*')?.eq('user_id', userId)?.eq('month_year', currentMonth)?.order('created_at', { ascending: false })?.limit(1)?.maybeSingle(),
    supabase?.from('spending_categories')?.select('spent, monthly_limit')?.eq('user_id', userId),
    supabase?.from('financial_goals')?.select('current_amount')?.eq('user_id', userId)?.eq('goal_status', 'active'),
  ]);

  const budget = budgetRes?.data;
  const categories = categoriesRes?.data || [];
  const goals = goalsRes?.data || [];

  const totalSpent = categories?.reduce((s, c) => s + (parseFloat(c?.spent) || 0), 0);
  const totalBudget = budget?.total_budget || 0;
  const remaining = totalBudget - totalSpent;
  const totalSavings = goals?.reduce((s, g) => s + (parseFloat(g?.current_amount) || 0), 0);

  return {
    monthlyBudget: totalBudget,
    totalSpent,
    remaining,
    totalSavings,
    monthYear: budget?.month_year || currentMonth,
  };
}

// ─── Spending Trends (last 6 months) ─────────────────────────────────────────
export async function fetchSpendingTrends(userId) {
  const months = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date();
    d?.setMonth(d?.getMonth() - i);
    months?.push(d?.toISOString()?.slice(0, 7));
  }

  const { data: budgets, error } = await supabase?.from('budgets')?.select('month_year, total_budget')?.eq('user_id', userId)?.in('month_year', months);

  if (error) throw error;

  const { data: txData } = await supabase?.from('transactions')?.select('amount, transaction_date, transaction_type')?.eq('user_id', userId)?.eq('transaction_type', 'expense')?.gte('transaction_date', months?.[0] + '-01');

  const budgetMap = {};
  (budgets || [])?.forEach((b) => { budgetMap[b.month_year] = b?.total_budget; });

  const spentMap = {};
  (txData || [])?.forEach((tx) => {
    const m = tx?.transaction_date?.slice(0, 7);
    if (m) spentMap[m] = (spentMap?.[m] || 0) + parseFloat(tx?.amount);
  });

  return months?.map((m) => ({
    month: new Date(m + '-01')?.toLocaleString('default', { month: 'short' }),
    budget: budgetMap?.[m] || 0,
    actual: Math.round(spentMap?.[m] || 0),
    savings: Math.round((budgetMap?.[m] || 0) - (spentMap?.[m] || 0)),
  }));
}
